// src/components/SupportPage.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  Grid
} from '@mui/material';
import {
  Support,
  Add,
  ArrowBack,
  Send,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  AssignmentTurnedIn
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import apiFetch from '../utils/api';

function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '' });
  const [replyMessage, setReplyMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('/api/tickets', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tickets');
      }

      const data = await response.json();
      setTickets(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      setError('Please provide both subject and message');
      return;
    }

    try {
      setSubmitting(true);
      const response = await apiFetch('/api/tickets', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTicket)
      });

      if (!response.ok) {
        throw new Error('Failed to create ticket');
      }

      const ticket = await response.json();
      setTickets([ticket, ...tickets]);
      setNewTicket({ subject: '', message: '' });
      setCreateDialogOpen(false);
      setError(null);
    } catch (err) {
      console.error('Error creating ticket:', err);
      setError('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await apiFetch(`/api/tickets/${selectedTicket._id}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: replyMessage })
      });

      if (!response.ok) {
        throw new Error('Failed to send reply');
      }

      const updatedTicket = await response.json();
      setSelectedTicket(updatedTicket);
      setTickets(tickets.map(t => t._id === updatedTicket._id ? updatedTicket : t));
      setReplyMessage('');
      setError(null);
    } catch (err) {
      console.error('Error sending reply:', err);
      setError('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      open: { label: 'Open', icon: <HourglassEmpty />, color: 'primary' },
      pending: { label: 'Pending', icon: <HourglassEmpty />, color: 'warning' },
      resolved: { label: 'Resolved', icon: <CheckCircle />, color: 'success' },
      closed: { label: 'Closed', icon: <AssignmentTurnedIn />, color: 'default' }
    };

    const config = statusConfig[status] || statusConfig.open;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Ticket Detail View
  if (selectedTicket) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={() => setSelectedTicket(null)} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            {selectedTicket.subject}
          </Typography>
          <Box ml={2}>
            {getStatusChip(selectedTicket.status)}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="caption" color="textSecondary">
              Created {formatDistanceToNow(new Date(selectedTicket.createdAt), { addSuffix: true })}
            </Typography>
          </CardContent>
        </Card>

        {/* Messages */}
        <List sx={{ mb: 2 }}>
          {selectedTicket.messages.map((msg, index) => (
            <React.Fragment key={msg._id || index}>
              <Paper
                elevation={msg.authorType === 'admin' ? 2 : 0}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: msg.authorType === 'admin' ? 'action.hover' : 'background.paper',
                  borderLeft: msg.authorType === 'admin' ? '4px solid' : 'none',
                  borderColor: 'primary.main'
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {msg.authorType === 'admin' ? 'Support Team' : msg.author?.username || 'You'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
              </Paper>
            </React.Fragment>
          ))}
        </List>

        {/* Reply Box */}
        {selectedTicket.status !== 'closed' && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Add Reply</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                placeholder="Type your reply..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                disabled={submitting}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
                onClick={handleReply}
                disabled={submitting || !replyMessage.trim()}
              >
                Send Reply
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  }

  // Ticket List View
  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
              Support
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Create and manage support tickets. Our team will respond as soon as possible.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New Ticket
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tickets.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Support sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No Support Tickets Yet
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              Create a ticket to get help from our support team.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Your First Ticket
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {tickets.map((ticket) => (
            <Grid item xs={12} key={ticket._id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 }
                }}
                onClick={() => setSelectedTicket(ticket)}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">{ticket.subject}</Typography>
                    {getStatusChip(ticket.status)}
                  </Box>
                  <Typography variant="body2" color="textSecondary" noWrap>
                    {ticket.messages[0]?.content}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Typography variant="caption" color="textSecondary">
                      {ticket.messages.length} {ticket.messages.length === 1 ? 'message' : 'messages'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Updated {formatDistanceToNow(new Date(ticket.updatedAt), { addSuffix: true })}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Ticket Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !submitting && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Support Ticket</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Subject"
            fullWidth
            variant="outlined"
            value={newTicket.subject}
            onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
            disabled={submitting}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={newTicket.message}
            onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateTicket}
            variant="contained"
            disabled={submitting || !newTicket.subject.trim() || !newTicket.message.trim()}
          >
            {submitting ? <CircularProgress size={24} /> : 'Create Ticket'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SupportPage;
